import React, { type FC, useState } from 'react'
import styled from '@emotion/styled'
import { fundsManagementTracker } from '@mobi/account/Common/Analytics'
import { Accordion } from '@mobi/component-library/Base/Accordion'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Tray } from '@mobi/component-library/Overlays/Tray'

type Props = {
  location: 'Deposit' | 'Withdrawals'
  asTray?: boolean
}

const PayIDInstructions: FC<Props> = ({ location, asTray }) => {
  const [isTrayOpen, setIsTrayOpen] = useState(false)

  const title = 'How to use PayID'
  const content = (
    <ContentStyled>
      <ol>
        <li>Generate your unique TABtouch PayID</li>
        <li>Copy your PayID</li>
        <li>Log in to your banking app</li>
        <li>Select PayID or payee via email address</li>
        <li>Paste your TABtouch PayID and finalise the payment</li>
      </ol>
      <p>
        Your PayID is unique to your account. Please ensure you copy the PayID field accurately
        before entering it into your banking app.
      </p>
      <p>
        If your banking app does not explicitly state PayID, look for the option to transfer via
        email address to complete the transfer.
      </p>
      <p>
        PayID is an instant transaction, however your bank may delay the first deposit from your
        bank account due to their own security controls.
      </p>
    </ContentStyled>
  )

  if (asTray) {
    return (
      <>
        <Button
          size='xs'
          color='secondary_grey'
          onClick={() => {
            fundsManagementTracker({
              interaction_type: 'How to use PayID',
              location,
            })
            setIsTrayOpen(true)
          }}
        >
          {title}
        </Button>
        <Tray
          isOpen={isTrayOpen}
          headerTitle={title}
          onDismiss={() => setIsTrayOpen(false)}
          trayStyle={{ paddingBottom: '4rem' }}
        >
          {content}
        </Tray>
      </>
    )
  }

  return (
    <ContainerStyled>
      <Accordion
        title={title}
        onToggle={isExpanded => {
          if (!isExpanded) {
            fundsManagementTracker({
              interaction_type: 'How to use PayID',
              location,
            })
          }
        }}
      >
        {content}
      </Accordion>
    </ContainerStyled>
  )
}

PayIDInstructions.displayName = 'PayIDInstructions'

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  marginTop: spacing.md,
})

const ContentStyled = styled.div({
  paddingBottom: spacing.smx2,
  color: colors.neutral[900],
  ...font.size.md,

  ol: {
    marginTop: 0,
    paddingLeft: spacing.none,
    marginLeft: '2ch',
    lineHeight: '2.5rem',
  },
  p: {
    marginTop: spacing.big,
    textAlign: 'left',
    ...font.size.md,
  },
})

export default PayIDInstructions
